import { useLingui } from '@lingui/react/macro';

import { CustomOrganizationPrice, PriceAmountWithDetails } from '@/api';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import IconEdit from '@/icons/Edit.svg';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { MarginTooltip } from '../ActionsList/MarginTooltip';
import { PriceWithDetailsTooltipWithConversions } from '../PriceWithDetails/PriceWithDetails';
import { PriceDetailsRowsOptions } from '../PriceWithDetails/usePriceDetailsRows';

export const DisplayPrice = ({
  line,
  type,
  fontWeight,
  price,
  isEdited,
  isInvalid,
  isEditable,
  isDisabled,
  customOrganizationPrice,
  sparePartPricesForBase,
  exchangeCatalogBreakdown,
  onPressEdit,
  isAnotherLineEditable,
}: {
  type: 'workshop' | 'organization';
  line: 'spareParts' | 'labor' | 'total' | 'exchange';
  fontWeight: 'regular' | 'medium';
  price?: PriceAmountWithDetails | null;
  isEdited?: boolean;
  isInvalid?: boolean;
  isEditable?: boolean;
  isDisabled?: boolean;
  customOrganizationPrice?: CustomOrganizationPrice | null;
  sparePartPricesForBase?: PriceDetailsRowsOptions['sparePartPricesForBase'];
  exchangeCatalogBreakdown?: PriceDetailsRowsOptions['exchangeCatalogBreakdown'];
  onPressEdit?: () => void;
  isAnotherLineEditable?: boolean;
}) => {
  const { view: requestView } = useRequestContext();
  const { t } = useLingui();

  // TODO: migrate this to typographic components while migrating <PriceWithDetailsTooltip>
  const className = fontWeight === 'regular' ? 'paragraph-100-regular' : 'paragraph-100-medium';
  const style = isInvalid
    ? { color: 'var(--color-danger-700)' }
    : isEdited
      ? { color: 'var(--color-tertiary-700)' }
      : isDisabled
        ? { color: 'var(--color-text-disabled-black)' }
        : undefined;

  return (
    <Stack
      row
      gap="0.5rem"
      flexWrap="nowrap"
      alignItems="center"
      justifyContent="flex-end"
      ariaLabel={
        type === 'workshop' ? requestView.workshopPrice.label : requestView.organizationPrice.label
      }
    >
      {price ? (
        <Stack row gap="0.25rem" flexWrap="nowrap" alignItems="center">
          <PriceWithDetailsTooltipWithConversions
            price={price}
            options={{
              showAmountBeforeTaxes:
                type === 'workshop'
                  ? requestView.workshopPrice.showAmountBeforeTaxes
                  : requestView.organizationPrice.showAmountBeforeTaxes,
              sparePartPricesForBase,
              exchangeCatalogBreakdown,
            }}
            className={className}
            style={style}
            direction="row-reverse"
            marginTooltip={
              (line === 'labor' || line === 'exchange') &&
              type === 'organization' && (
                <MarginTooltip customOrganizationPrice={customOrganizationPrice} />
              )
            }
          />
        </Stack>
      ) : (
        <p className={className} style={style}>
          -
        </p>
      )}
      {isEditable && (
        <Button
          variant="secondary"
          size="small"
          ariaLabel={t({ id: 'article.form.actions.price.edit', message: 'Edit' })}
          tooltip={t({ id: 'article.form.actions.price.edit', message: 'Edit' })}
          iconOnly
          onPress={onPressEdit}
        >
          <IconEdit />
        </Button>
      )}
      {!isEditable && isAnotherLineEditable && <div style={{ paddingLeft: '1.5rem' }} />}
    </Stack>
  );
};
